
import { useDoctorFetching } from './doctor/useDoctorFetching';
import { useDoctorFormState } from './doctor/useDoctorFormState';
import { useDoctorDialogs } from './doctor/useDoctorDialogs';
import { useImageHandling } from './doctor/useImageHandling';
import { useDoctorActions } from './doctor/useDoctorActions';
import { Doctor } from '@/types';

export const useDoctorManagement = () => {
  // Compose smaller hooks to build the complete management functionality
  const {
    doctors, 
    setDoctors,
    isLoading, 
    setIsLoading,
    connectionError, 
    setConnectionError,
    loadDoctors
  } = useDoctorFetching();
  
  const {
    currentDoctor,
    setCurrentDoctor,
    newDoctor,
    setNewDoctor,
    handleInputChange,
    resetForm
  } = useDoctorFormState();
  
  const {
    isEditDialogOpen,
    setIsEditDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isScheduleDialogOpen,
    setIsScheduleDialogOpen
  } = useDoctorDialogs();
  
  const {
    imageFile,
    setImageFile,
    imagePreview,
    setImagePreview,
    handleFileChange,
    handleImageUrlChange: baseHandleImageUrlChange
  } = useImageHandling();
  
  // Wrap the image URL change handler to integrate with our form state
  const handleImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    baseHandleImageUrlChange(e, (name, value) => {
      setNewDoctor(prev => ({ ...prev, [name]: value }));
    });
  };
  
  const {
    handleAddDoctor,
    handleUpdateDoctor,
    handleDeleteDoctor,
    handleCreateSampleRecords
  } = useDoctorActions(
    setDoctors,
    setConnectionError,
    setIsLoading,
    setIsEditDialogOpen,
    setIsDeleteDialogOpen,
    setIsScheduleDialogOpen, // Added this parameter
    newDoctor,
    currentDoctor,
    imageFile,
    resetForm
  );
  
  // Create wrapped versions of functions that need to access multiple hooks
  const handleEditDoctor = (doctor: Doctor) => {
    setCurrentDoctor(doctor);
    setNewDoctor(doctor);
    setImagePreview(doctor.image || '');
    setIsEditDialogOpen(true);
  };
  
  const handleDeletePrompt = (doctor: Doctor) => {
    setCurrentDoctor(doctor);
    setIsDeleteDialogOpen(true);
  };
  
  const handleScheduleManagement = (doctor: Doctor) => {
    setCurrentDoctor(doctor);
    setIsScheduleDialogOpen(true);
  };

  return {
    doctors,
    isLoading,
    connectionError,
    currentDoctor,
    newDoctor,
    imageFile,
    imagePreview,
    isEditDialogOpen,
    isDeleteDialogOpen,
    isScheduleDialogOpen,
    handleFileChange,
    handleImageUrlChange,
    handleInputChange,
    handleAddDoctor,
    handleEditDoctor,
    handleUpdateDoctor,
    handleDeletePrompt,
    handleDeleteDoctor,
    handleScheduleManagement,
    handleCreateSampleRecords,
    loadDoctors,
    setIsEditDialogOpen,
    setIsDeleteDialogOpen,
    setIsScheduleDialogOpen,
    resetForm
  };
};
